/* eslint-disable react/jsx-pascal-case */
import React, { useCallback, useMemo } from "react";
import {useDispatch, useSelector} from "react-redux";
import { useNavigate } from "react-router-dom";
import {Formik} from "formik";
import { useInitialize } from "../../../util/react.js";
import {Translation as T} from "@awe-web/shared/lib";
import {B2B_LTR_SelectRentObject} from "../../../routing/routes.js";

import {ScreenSection, ScreenHeading, ScreenLoading} from "../../../components/screen.js";
import {TypeArea} from "../../../components/layout.js";
import SelectedStationAndTime from "../../../stations/components/selected_station_and_time.jsx";
import SelectedRentObject from "../../../rent_objects/components/selected_rent_object.jsx";
import {Row, Col} from "../../../components/grid.js";
import {Button} from "../../../components/form.js";
import {
  getChangedLtrMileageOptionMessage,
  getLtrSelectedMileageOptionId,
  getLtrDeductibleOptions,
  getLtrSelectedEquipmentOptions,
  getChangedEquipmentOptionsMessage
} from "../../../rent_contracts/store.js";
import LTR_SelectExtrasForm from "../../../rent_contracts/components/ltr_select_extras_form.jsx";
import {initialize, submit} from "./actions.js";

import "./select_extras.scss";

const BACK = "ALG0000030";
const SCREEN_HEADING = "FSD0000125";
const PRICES_NOTICE = "ALG0000029";
const EXCLUDING_VAT = "ALG0000245";

function InternalSelectExtrasForm({onSubmit}) {
  const deductibleOptions = useSelector((state) => getLtrDeductibleOptions(state));
  const selectedMileageOption = useSelector((state) => getLtrSelectedMileageOptionId(state));
  const selectedEquipmentOptions = useSelector((state) => getLtrSelectedEquipmentOptions(state));
  const mileageOptionChangedMessage = useSelector((state) => getChangedLtrMileageOptionMessage(state));
  const equipmentOptionsChangedMessage = useSelector((state) => getChangedEquipmentOptionsMessage(state));

  const enabledDeductible = useMemo(() => {
    return deductibleOptions.toJS().find(({enabled}) => enabled);
  }, [deductibleOptions]);

  const initialValues = {
    mileage: selectedMileageOption,
    deductible: enabledDeductible && enabledDeductible.id,
    equipment: selectedEquipmentOptions.toJS()
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {(formikProps) => (
        <LTR_SelectExtrasForm
          {...formikProps}
          reservationPath="B2B"
          deductibleOptions={deductibleOptions.toJS()}
          totalPriceHintCode={EXCLUDING_VAT}
          mileageOptionChangedMessage={mileageOptionChangedMessage}
          equipmentOptionsChangedMessage={equipmentOptionsChangedMessage}
        />
      )}
    </Formik>
  );
}

export default function B2B_LTR_SelectExtras() {
  
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onSubmit = useCallback(async (values) => {
    const nextPath = await dispatch(submit(values));
    if (nextPath && nextPath.length > 0) {
      navigate(nextPath);
    }
  }, [dispatch, navigate]);

  const init = useCallback(async () => {
    const redirectPath = await dispatch(initialize());

    if (redirectPath && redirectPath.length > 0) {
      navigate(redirectPath);
    }
  }, [dispatch, navigate]);

  const isReady = useInitialize(init);
  if (!isReady) {
    return <ScreenLoading />;
  }

  return (
    <div className="b2b-ltr-select-extras">
      <ScreenSection dark>
        <TypeArea>
          <SelectedStationAndTime reservationPath="B2B" />
        </TypeArea>
      </ScreenSection>

      <ScreenSection light>
        <TypeArea>
          <SelectedRentObject
            className="b2b-ltr-select-extras__selected-rent-object"
            selectedBasicProperties="B2B"
            action={(
              <Button secondary back to={B2B_LTR_SelectRentObject.path()}>
                <T code={BACK} />
              </Button>
            )}
          />
        </TypeArea>
      </ScreenSection>

      <ScreenSection className="b2b-ltr-select-extras__title">
        <TypeArea>
          <Row>
            <Col desktop={{offset: 1, cols: 10}}>
              <ScreenHeading className="b2b-ltr-select-extras__screen-heading">
                <T code={SCREEN_HEADING} />
              </ScreenHeading>
            </Col>
          </Row>
        </TypeArea>
      </ScreenSection>

      <InternalSelectExtrasForm onSubmit={onSubmit}/>

      <TypeArea>
        <ScreenSection className="b2b-ltr-select-extras__prices-notice">
          <Row>
            <Col desktop={{offset: 1}}>
              <T code={PRICES_NOTICE} />
            </Col>
          </Row>
        </ScreenSection>
      </TypeArea>
    </div>
  );
}
